import { GoogleCalendarClient, type GoogleCalendarListEntry } from "@app/storage";
import type { SyncDirection } from "@app/core";
import { Button, Calendar as CalendarIcon, Card, Link, RefreshCw, Unlink } from "@app/ui";
import { useCallback, useEffect, useState } from "react";
import {
  useCalendarsStore,
  useGoogleAuthStore,
  useGoogleSyncStore,
  toast,
} from "../../stores/index.js";
import { googleConfigured } from "../../lib/google-config.js";
import { useI18n } from "../../lib/useI18n.js";

/**
 * Connect a Google account and bind its calendars to in-app calendars for live
 * two-way sync. Sync itself runs in the background (`useGoogleSyncReconciler`);
 * this card manages the connection + per-calendar bindings. The PKCE flow runs
 * in the web app only for now (desktop/Android redirect capture is a follow-up).
 */
export function GoogleCalendarCard() {
  const { t } = useI18n();
  const connected = useGoogleAuthStore((s) => s.connected);
  const email = useGoogleAuthStore((s) => s.email);
  const authBusy = useGoogleAuthStore((s) => s.busy);
  const loaded = useGoogleAuthStore((s) => s.loaded);
  const load = useGoogleAuthStore((s) => s.load);
  const connect = useGoogleAuthStore((s) => s.connect);
  const disconnect = useGoogleAuthStore((s) => s.disconnect);

  const calendars = useCalendarsStore((s) => s.calendars);
  const addCalendar = useCalendarsStore((s) => s.add);
  const bindGoogle = useCalendarsStore((s) => s.bindGoogle);
  const unbindExternal = useCalendarsStore((s) => s.unbindExternal);
  const setSyncDirection = useCalendarsStore((s) => s.setSyncDirection);

  const syncBusy = useGoogleSyncStore((s) => s.busy);
  const syncNow = useGoogleSyncStore((s) => s.syncNow);

  const [remote, setRemote] = useState<GoogleCalendarListEntry[] | null>(null);
  const [listing, setListing] = useState(false);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  const refreshList = useCallback(async () => {
    if (!useGoogleAuthStore.getState().connected) return;
    setListing(true);
    try {
      const client = new GoogleCalendarClient(
        () => useGoogleAuthStore.getState().getAccessToken(),
        () => useGoogleAuthStore.getState().refreshAccess(),
      );
      setRemote(await client.listCalendars());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setListing(false);
    }
  }, []);

  useEffect(() => {
    if (connected) void refreshList();
    else setRemote(null);
  }, [connected, refreshList]);

  if (!googleConfigured()) {
    return (
      <Card className="p-4">
        <h2 className="mb-2 font-medium">{t("sync.googleCalendar")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("sync.googleNotConfigured")}
        </p>
      </Card>
    );
  }

  const boundFor = (gcalId: string) =>
    calendars.find((c) => c.external_source === "google" && c.external_calendar_id === gcalId && !c.deleted_at);

  async function bind(g: GoogleCalendarListEntry) {
    const direction: SyncDirection = g.accessRole === "reader" || g.accessRole === "freeBusyReader" ? "pull" : "both";
    const created = await addCalendar(g.summary || t("sync.googleCalendar"), g.backgroundColor ?? null);
    await bindGoogle(created.id, {
      externalCalendarId: g.id,
      externalAccount: email ?? "",
      direction,
    });
    void syncNow({ silent: true });
  }

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-medium">{t("sync.googleCalendar")}</h2>
        {connected ? (
          <Button variant="outline" size="sm" disabled={syncBusy} onClick={() => void syncNow()}>
            <RefreshCw className={`mr-2 h-4 w-4 ${syncBusy ? "animate-spin" : ""}`} /> {t("sync.syncNow")}
          </Button>
        ) : null}
      </div>

      {!connected ? (
        <>
          <p className="mb-2 text-sm text-muted-foreground">
            {t("sync.googleConnectDescription")}
          </p>
          <p className="mb-3 text-xs text-muted-foreground">
            {t("sync.googleSeparateNote")}
          </p>
          <Button disabled={authBusy} onClick={() => void connect()}>
            {authBusy ? t("sync.connecting") : t("sync.connectGoogle")}
          </Button>
        </>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {t("sync.connectedAs", { email: "" })}<span className="font-medium text-foreground">{email ?? t("sync.googleAccount")}</span>
            </p>
            <Button variant="outline" size="sm" onClick={() => void disconnect()}>
              {t("sync.disconnect")}
            </Button>
          </div>

          <p className="mb-3 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            {t("sync.googleEncoding.tasks")}{" "}
            {t("sync.googleEncoding.subtasks")}
          </p>

          {listing && !remote ? (
            <p className="text-sm text-muted-foreground">{t("sync.loadingCalendars")}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {(remote ?? []).map((g) => {
                const bound = boundFor(g.id);
                return (
                  <li key={g.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm">{g.summary || g.id}</span>
                      {g.primary ? <span className="text-xs text-muted-foreground">{t("sync.primary")}</span> : null}
                    </div>
                    {bound ? (
                      <div className="flex items-center gap-2">
                        <select
                          className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                          value={bound.sync_direction}
                          onChange={(e) => void setSyncDirection(bound.id, e.target.value as SyncDirection)}
                        >
                          <option value="both">{t("sync.twoWay")}</option>
                          <option value="pull">{t("sync.googleToApp")}</option>
                          <option value="push">{t("sync.appToGoogle")}</option>
                          <option value="off">{t("sync.paused")}</option>
                        </select>
                        <Button variant="outline" size="sm" onClick={() => void unbindExternal(bound.id)}>
                          <Unlink className="mr-1 h-3.5 w-3.5" /> {t("common.remove")}
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => void bind(g)}>
                        <Link className="mr-1 h-3.5 w-3.5" /> {t("sync.addToApp")}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
